import React from 'react';

import { cx, Drawer } from '@bolteu/clay-react';
import { Popover } from '@provider-portal/design-common/clay/components/Popover';
import { useBreakpointUp } from '@provider-portal/design-common/clay/hooks/useBreakpoint';

import { StoreSelectorContent } from './StoreSelectorContent';
import { StoreSelectorCurrentStore } from './StoreSelectorCurrentStore';
import { Availability, StoreListGroup } from './StoreSelectorStoresList';

export interface StoreSelectorProps {
  parentName: string;
  storeName: string;
  stores: StoreListGroup[];
  storeAvailabilities: Map<number, Availability>;
  storeAvailabilitiesLoading: boolean;
  selectedStoreId?: number;
  showAddVenueButton: boolean;
  onAddVenue?: () => void;
  onSelectStore?: (storeId: number) => void;
  onSelectorOpen?: () => void;
  onSearchPerformed?: (searchQuery: string, resultsCount: number, searchCount: number) => void;
}

export function StoreSelector({
  parentName,
  storeName,
  stores,
  storeAvailabilities,
  storeAvailabilitiesLoading,
  selectedStoreId,
  showAddVenueButton,
  onAddVenue,
  onSelectStore,
  onSelectorOpen,
  onSearchPerformed,
}: StoreSelectorProps) {
  const isDesktop = useBreakpointUp('sm');
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open && onSelectorOpen) {
        onSelectorOpen();
      }
    },
    [onSelectorOpen],
  );

  const handeAddVenue = React.useCallback(() => {
    if (onAddVenue) {
      onAddVenue();
    }
    setIsOpen(false);
  }, [onAddVenue]);

  const handleSelectStore = React.useCallback(
    (storeId: number) => {
      if (onSelectStore) {
        onSelectStore(storeId);
      }
      setIsOpen(false);
    },
    [onSelectStore],
  );

  const trigger = <StoreSelectorCurrentStore parentName={parentName} storeName={storeName} />;
  const content = (
    <StoreSelectorContent
      selectedStoreId={selectedStoreId}
      stores={stores}
      storeAvailabilities={storeAvailabilities}
      storeAvailabilitiesLoading={storeAvailabilitiesLoading}
      onSelectStore={handleSelectStore}
      showAddVenueButton={showAddVenueButton}
      onAddVenue={handeAddVenue}
      onSearchPerformed={onSearchPerformed}
    />
  );

  return (
    <div className={cx('flex items-center gap-2', isDesktop && 'w-full')}>
      {isDesktop ? (
        <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
          <Popover.Trigger render={trigger} />
          <Popover.Portal>
            <Popover.Positioner side="bottom" align="end" sideOffset={0} className="z-[1300]">
              <Popover.Popup
                id="store-selector-menu"
                className="h-[556px] max-h-[calc(100dvh-32px)] w-80 overflow-y-auto rounded-2xl bg-layer-floor-1 text-primary shadow-lg outline-none"
              >
                {content}
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
      ) : (
        <Drawer.Root open={isOpen} onOpenChange={handleOpenChange} swipeDirection="down">
          <Drawer.Trigger render={trigger} />
          <Drawer.Portal>
            <Drawer.Backdrop />
            <Drawer.Viewport>
              <Drawer.Popup className="h-[calc(100dvh-200px)] max-h-[556px] min-w-80 rounded-t-2xl px-6 shadow-none [--bleed:0rem]">
                {content}
              </Drawer.Popup>
            </Drawer.Viewport>
          </Drawer.Portal>
        </Drawer.Root>
      )}
    </div>
  );
}
